import React, { useEffect, useRef } from 'react';
import moment from 'moment';
import { styled } from '@mui/material';
import uuid from 'uuid';

import { UploadResourcesModal } from 'components/organisms/Task';
import { useSnackbar } from 'contexts/snackbar';
import { useUser2 } from 'contexts/user2';
import {
    Box,
    Button,
    CollaboratorInput,
    DatePicker,
    FormLabel,
    Icon,
    MenuItem,
    ResourceChip,
    RichTextArea,
    Select,
    Stack,
    Tag,
    Text,
    TextInput,
} from 'shell/components';
import {
    DEFAULT_SUBTASK_TITLE,
    DEFAULT_TASK_TITLE,
    TASK_STATUS_OPTIONS,
    TASK_WORKLOAD_OPTIONS,
    TYPES,
    ACTIVITY_ACTIONS,
} from 'utils/constants';
import {
    downloadFileFromURL,
    getAbsoluteURL,
    getS3FileURL,
    getTooltipComponent,
    getUserPreferredFullName,
} from 'utils/helper';

const tooltipTexts = {
    owner: { title: 'Owner', description: 'Who is responsible for ensuring this task is completed?' },
    collaborators: {
        title: 'Collaborators',
        description: 'Who is working on this? Collaborators will split task points equally.',
    },
    workload: {
        text: {
            title: 'Workload',
            description: 'An estimation of the time and effort it will take to complete this task',
        },
        select: {
            low: { title: 'Workload: Low', description: 'Low effort, time and/or resources to complete' },
            medium: { title: 'Workload: Medium', description: 'Moderate effort, time and/or resources to complete' },
            high: { title: 'Workload: High', description: 'Significant effort, time and/or resources to complete' },
        },
    },
    status: {
        text: { title: 'Status', description: 'What stage is this task in?' },
        select: {
            toDo: {
                title: '"To Do" Column',
                description:
                    'Tasks that have been identified and prioritized for completion, but have not been started',
            },
            inProgress: {
                title: '"In Progress" Column',
                description: 'Tasks that are actively being worked on by team members',
            },
            underReview: {
                title: '"Under Review" Column',
                description: 'Tasks that are waiting for review or approval before being considered "done"',
            },
            completed: {
                title: '"Completed" Column',
                description: 'Tasks that have been reviewed and are now complete.',
            },
        },
    },
    due_date: { title: 'Due Date', description: 'When should this task be completed?' },
    details: { title: 'Details', description: 'Add important details about the task here' },
    resources: { title: 'Resources', description: 'Resources helpful for task completion' },
    milestones: {
        title: 'Milestones',
        description: 'A milestone is a deliverable created by your instructor that requires a submission by your team',
    },
};

const TaskSection = ({
    device,
    currentAccountId,
    role,
    disabled = false,
    isDrawerOpen,
    type = 'task',
    task = {},
    taskList,
    fileList,
    sharedUrlList,
    assignableGroupMembers = [],
    courseInstructors = [],
    getUserByAccountId,
    updateTask: update,
    createTaskResource,
    deleteTaskResource,
    createActivity,
    resourcesModal,
    setResourcesModal,
}) => {
    const { currentGroupAccountUsers } = useUser2();
    const { message } = useSnackbar();

    const titleInputRef = useRef(null);

    const {
        id,
        created_on: createdOn,
        owner_account_id: ownerAccountId,
        title = '',
        collaborator_account_ids: collaboratorAccountIds,
        assignee_account_id: assigneeAccountId,
        workload = 'low',
        status = 'toDo',
        end_on: endOn,
        description = '',
        type: taskType,
        shouldCreateActivityOnBlur,
    } = task;

    const updateTask = (key, value) => {
        switch (key) {
            case 'owner': {
                if (value === assigneeAccountId) return;
                if (!collaboratorAccountIds?.includes(value)) {
                    update(id, 'collaborator_account_ids', [...(collaboratorAccountIds || []), value], true);
                }
                return update(id, 'assignee_account_id', value, true);
            }
            case 'collaborators': {
                return update(id, 'collaborator_account_ids', value, true);
            }
            case 'milestone': {
                return update(id, 'milestone_id', value === 'none' ? '' : value);
            }
            case 'workload': {
                if (value === workload) return;
                return update(id, 'workload', value);
            }
            case 'status': {
                if (value === status) return;
                return update(id, 'status', value, true);
            }
            case 'dueDate': {
                return update(id, 'end_on', value);
            }
            case 'details': {
                return update(id, 'description', value);
            }
        }
    };

    const handleBlurTitle = (event) => {
        if (event.target.value.trim() === '' && title === DEFAULT_SUBTASK_TITLE) {
            event.target.value = DEFAULT_SUBTASK_TITLE;
        }
        if (event.target.value && shouldCreateActivityOnBlur) {
            createActivity({ id, title }, TYPES.TASK, ACTIVITY_ACTIONS.CREATE);
            update(id, 'title', event.target.value, true);
            update(id, 'shouldCreateActivityOnBlur', false);
        }
    };

    const handleChangeTitle = (value) => {
        return update(id, 'title', value);
    };

    const handleFocusTitle = (event) => {
        if (
            (type === 'subtask' && event.target.value === DEFAULT_SUBTASK_TITLE) ||
            (type === 'task' && event.target.value === DEFAULT_TASK_TITLE)
        ) {
            event.target.value = '';
        }
    };

    const handleAddResources = (files, link) => {
        let resources = [];
        const fileResources = files.map((file) => ({ data: file }));
        const linkResource = { ...link };
        resources = resources.concat(fileResources);
        if (linkResource.url && linkResource.name) resources = resources.concat(linkResource);
        resources = resources.map((resource) => ({
            ...resource,
            id: uuid.v4(),
            owner_account_id: currentAccountId,
            type: TYPES.TASK,
            task_id: id,
        }));
        resources.forEach((resource) => createTaskResource(id, resource));
        setResourcesModal((current) => ({ ...current, isOpen: false }));
    };

    const renderOwnerSelect = () => {
        let assigneeOptions = assignableGroupMembers.map((accountUser) => ({
            key: accountUser.owner_account_id,
            value: getUserPreferredFullName(accountUser),
        }));
        let currentAssignee = assigneeOptions.find(({ key }) => key === assigneeAccountId);
        if (!currentAssignee) {
            assigneeOptions = [{ key: 'unassigned', value: 'Unassigned' }, ...assigneeOptions];
            currentAssignee = { key: 'unassigned', value: 'Unassigned' };
        }
        return (
            <CustomSelect
                disabled={disabled}
                inputProps={{ id: 'task-owner' }}
                value={currentAssignee.key}
                onChange={(e) => updateTask('owner', e.target.value)}
            >
                {assigneeOptions.map((assigneeOption) => (
                    <MenuItem key={assigneeOption.key} value={assigneeOption.key}>
                        {assigneeOption.value}
                    </MenuItem>
                ))}
            </CustomSelect>
        );
    };

    const renderCollaboratorInput = () => {
        return (
            <CollaboratorInput
                id={'task-collaborators'}
                disabled={disabled}
                options={currentGroupAccountUsers}
                value={collaboratorAccountIds || []}
                onChange={(value) => updateTask('collaborators', value)}
            />
        );
    };

    const renderMilestoneSelect = () => {
        const milestones = Object.values(taskList.all).filter(({ type }) => type === TYPES.MILESTONE);
        const options = [
            { key: 'none', value: 'None' },
            ...milestones.map((milestone) => ({ key: milestone.milestone_id, value: milestone.title })),
        ];
        return (
            <CustomSelect
                data-cy={'task-section-milestone'}
                disabled={disabled}
                inputProps={{ id: 'task-milestone' }}
                value={task.milestone_id || 'none'}
                onChange={(e) => updateTask('milestone', e.target.value)}
            >
                {options.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                        {option.value}
                    </MenuItem>
                ))}
            </CustomSelect>
        );
    };

    const renderWorkloadSelect = () => {
        const options = TASK_WORKLOAD_OPTIONS.map((option) => ({
            key: option.value,
            value: (
                <Tag
                    sx={{ width: '100%' }}
                    tooltip={{
                        component: getTooltipComponent(
                            tooltipTexts.workload.select[option.value].title,
                            tooltipTexts.workload.select[option.value].description
                        ),
                        placement: 'left',
                    }}
                    type={'workload'}
                    value={option.value}
                />
            ),
        }));
        return (
            <CustomSelect
                disabled={disabled}
                inputProps={{ id: 'task-workload' }}
                renderValue={(selected) => {
                    return options.find(({ key }) => key === selected).value;
                }}
                value={workload}
                onChange={(e) => updateTask('workload', e.target.value)}
            >
                {options.map(({ key, value }) => (
                    <MenuItem key={key} value={key}>
                        {value}
                    </MenuItem>
                ))}
            </CustomSelect>
        );
    };

    const renderStatusSelect = () => {
        return (
            <CustomSelect
                data-cy={'task-section-status'}
                disabled={disabled}
                inputProps={{ id: 'task-status' }}
                value={status}
                onChange={(e) => updateTask('status', e.target.value)}
            >
                {TASK_STATUS_OPTIONS.map((option) => (
                    <MenuItem
                        data-cy={`task-section-status-${option.value}`}
                        key={option.value}
                        tooltip={{
                            component: getTooltipComponent(
                                tooltipTexts.status.select[option.value].title,
                                tooltipTexts.status.select[option.value].description
                            ),
                            placement: 'left',
                        }}
                        value={option.value}
                    >
                        {option.title}
                    </MenuItem>
                ))}
            </CustomSelect>
        );
    };

    const renderDueDateSelect = () => {
        return (
            <CustomDatePicker
                id={'task-datepicker'}
                disabled={disabled}
                value={endOn ? moment(endOn) : moment()}
                onChange={(timestamp) => updateTask('dueDate', timestamp.valueOf())}
            />
        );
    };

    const renderResources = () => {
        const handleSelect = ({ key }, resource) => {
            switch (key) {
                case 'download':
                    downloadFileFromURL(getS3FileURL(resource.data), resource.data.name);
                    break;
                case 'visit':
                    downloadFileFromURL(getAbsoluteURL(resource.url));
                    break;
                case 'copy':
                    navigator.clipboard.writeText(getAbsoluteURL(resource.url));
                    message.success('Link copied to clipboard.');
                    break;
                case 'delete':
                    deleteTaskResource(resource.type, resource.id);
                    break;
                default:
                    break;
            }
        };

        const handleAddClick = () => {
            setResourcesModal((current) => ({ ...current, isOpen: true }));
        };

        const fileResources = Object.values(fileList).filter((file) =>
            [TYPES.TASK, TYPES.TEMPLATE, TYPES.MILESTONE].includes(file.type)
        );
        const sharedUrlResources = Object.values(sharedUrlList).filter((sharedUrl) =>
            [TYPES.TASK, TYPES.TEMPLATE, TYPES.MILESTONE].includes(sharedUrl.type)
        );
        const resources = [...fileResources, ...sharedUrlResources]
            .map((resource) => ({ ...resource, origin: resource.type, type: resource.data ? 'file' : 'link' }))
            .sort((a, b) => a.created_on - b.created_on);

        return (
            <Stack sx={{ minWidth: 0 }} alignItems={'flex-start'} spacing={2}>
                {resources.length ? (
                    <Stack sx={{ flexWrap: 'wrap', maxWidth: '100%' }} spacing={2}>
                        {resources.map((resource) => {
                            const isInstructorCreated = courseInstructors.some(
                                (instructor) => instructor.owner_account_id === resource.owner_account_id
                            );
                            const isOwner = currentAccountId === resource.owner_account_id;
                            return (
                                <ResourceChip
                                    data-cy={resource.name || resource.data.name}
                                    key={resource.id}
                                    options={[
                                        ...(resource.type === 'file'
                                            ? [{ key: 'download', value: 'Download' }]
                                            : [
                                                  { key: 'visit', value: 'Visit Link' },
                                                  { key: 'copy', value: 'Copy Link' },
                                              ]),
                                        ...[
                                            {
                                                key: 'delete',
                                                value: 'Delete',
                                                disabled: !(taskType !== TYPES.MILESTONE
                                                    ? true
                                                    : isOwner || !isInstructorCreated),
                                            },
                                        ],
                                    ]}
                                    resource={resource}
                                    size={'small'}
                                    type={resource.type}
                                    onSelect={(option) => handleSelect(option, resource)}
                                />
                            );
                        })}
                    </Stack>
                ) : null}
                <Button
                    disabled={disabled}
                    startIcon={<Icon kind={'add'} />}
                    variant={'tertiary'}
                    onClick={handleAddClick}
                >
                    Add Resources
                </Button>
            </Stack>
        );
    };

    const isInstructorCreated = courseInstructors.some((instructor) => instructor.owner_account_id === ownerAccountId);

    useEffect(() => {
        if (isDrawerOpen && title === '') {
            setTimeout(() => titleInputRef.current.focus(), 150);
        }
    }, [isDrawerOpen]);

    return (
        <>
            <RootStack id={'task-main-section'} spacing={2}>
                <TitleStack alignItems={'center'} direction={'row'} spacing={2}>
                    <TitleTextInput
                        data-cy={'task-title'}
                        key={id}
                        ref={titleInputRef}
                        defaultValue={title}
                        placeholder={'Try starting the title with a verb...'}
                        onBlur={handleBlurTitle}
                        onChange={(e) => handleChangeTitle(e.target.value)}
                        onFocus={handleFocusTitle}
                    />
                    {taskType === TYPES.MILESTONE && <Tag type={'task'} value={'milestone'} />}
                </TitleStack>
                {(type === TYPES.TASK || isInstructorCreated) && (
                    <CustomText color={'light'} component={'div'} variant={'p2'}>
                        {`Created on ${moment(createdOn).format('MMMM D[,] YYYY')} by `}
                        {isInstructorCreated && (
                            <>
                                <Tag type={'role'} value={'instructor'} />
                            </>
                        )}
                        {getUserPreferredFullName(getUserByAccountId(ownerAccountId))}
                    </CustomText>
                )}
                <CustomBox disabled={disabled}>
                    {taskType !== TYPES.MILESTONE && (
                        <>
                            <CustomFormLabel htmlFor={'task-owner'}>
                                <Text
                                    tooltip={{
                                        component: getTooltipComponent(
                                            tooltipTexts.owner.title,
                                            tooltipTexts.owner.description
                                        ),
                                    }}
                                    variant={'p2'}
                                    weight={'semibold'}
                                >
                                    Owner
                                </Text>
                            </CustomFormLabel>
                            {renderOwnerSelect()}
                        </>
                    )}
                    <>
                        <FormLabel
                            aria-disabled={disabled}
                            sx={{ mt: 4, width: 'fit-content' }}
                            htmlFor={'task-collaborators'}
                        >
                            <Text
                                tooltip={{
                                    component: getTooltipComponent(
                                        tooltipTexts.collaborators.title,
                                        tooltipTexts.collaborators.description
                                    ),
                                }}
                                variant={'p2'}
                                weight={'semibold'}
                            >
                                Collaborators
                            </Text>
                        </FormLabel>
                        {renderCollaboratorInput()}
                    </>
                    {taskType !== TYPES.MILESTONE && !task.parent_id && (
                        <>
                            <CustomFormLabel htmlFor={'task-owner'}>
                                <Text
                                    tooltip={{
                                        component: getTooltipComponent(
                                            tooltipTexts.milestones.title,
                                            tooltipTexts.milestones.description
                                        ),
                                    }}
                                    variant={'p2'}
                                    weight={'semibold'}
                                >
                                    Milestone
                                </Text>
                            </CustomFormLabel>
                            {renderMilestoneSelect()}
                        </>
                    )}
                    <CustomFormLabel htmlFor={'task-workload'}>
                        <Text
                            tooltip={{
                                component: getTooltipComponent(
                                    tooltipTexts.workload.text.title,
                                    tooltipTexts.workload.text.description
                                ),
                            }}
                            variant={'p2'}
                            weight={'semibold'}
                        >
                            Workload
                        </Text>
                    </CustomFormLabel>
                    {renderWorkloadSelect()}
                    {type === TYPES.TASK && (
                        <>
                            <CustomFormLabel htmlFor={'task-status'}>
                                <Text
                                    tooltip={{
                                        component: getTooltipComponent(
                                            tooltipTexts.status.text.title,
                                            tooltipTexts.status.text.description
                                        ),
                                    }}
                                    variant={'p2'}
                                    weight={'semibold'}
                                >
                                    Status
                                </Text>
                            </CustomFormLabel>
                            {renderStatusSelect()}
                        </>
                    )}
                    <CustomFormLabel htmlFor={'task-datepicker'}>
                        <Text
                            tooltip={{
                                component: getTooltipComponent(
                                    tooltipTexts.due_date.title,
                                    tooltipTexts.due_date.description
                                ),
                            }}
                            variant={'p2'}
                            weight={'semibold'}
                        >
                            Due Date
                        </Text>
                    </CustomFormLabel>
                    {renderDueDateSelect()}
                    <FormLabel
                        aria-disabled={disabled}
                        sx={{ mt: '10px', width: 'fit-content' }}
                        htmlFor={'task-description'}
                    >
                        <Text
                            tooltip={{
                                component: getTooltipComponent(
                                    tooltipTexts.details.title,
                                    tooltipTexts.details.description
                                ),
                            }}
                            variant={'p2'}
                            weight={'semibold'}
                        >
                            Details
                        </Text>
                    </FormLabel>
                    <RichTextArea
                        id={'task-description'}
                        key={id}
                        disabled={disabled}
                        defaultValue={description}
                        placeholder={type === TYPES.TASK ? 'Enter Task Details' : 'Enter Subtask Details'}
                        variant={'dynamic'}
                        onBlur={(html) => {
                            if (disabled) return;
                            updateTask('details', html.trim());
                        }}
                    />
                    {type === TYPES.TASK && (
                        <>
                            <FormLabel
                                aria-disabled={disabled}
                                sx={{ mt: '10px', width: 'fit-content' }}
                                htmlFor={'task-resources'}
                            >
                                <Text
                                    tooltip={{
                                        component: getTooltipComponent(
                                            tooltipTexts.resources.title,
                                            tooltipTexts.resources.description
                                        ),
                                    }}
                                    variant={'p2'}
                                    weight={'semibold'}
                                >
                                    Resources
                                </Text>
                            </FormLabel>
                            {renderResources()}
                        </>
                    )}
                </CustomBox>
            </RootStack>
            <UploadResourcesModal
                {...resourcesModal}
                device={device}
                submitResources={handleAddResources}
                onClose={() => setResourcesModal((current) => ({ ...current, isOpen: false }))}
            />
        </>
    );
};

const RootStack = styled(Stack)(({ theme }) => {
    return {
        marginTop: theme.spacing(2),
    };
});

const TitleStack = styled(Stack)(({ type, theme }) => {
    return {
        ...(type !== TYPES.MILESTONE && {
            marginLeft: theme.spacing(-3),
        }),
    };
});

const TitleTextInput = styled(TextInput)(({ theme }) => {
    return {
        flex: 1,
        '&& input': {
            fontSize: `${theme.typography.sh1.fontSize.large}rem`,
            fontWeight: theme.typography.sh1.fontWeight.semibold,
            letterSpacing: `${theme.typography.sh1.letterSpacing.large}rem`,
            lineHeight: `${theme.typography.getLineHeight('sh1', 'large')}rem`,
        },
        '& fieldset': {
            border: 'none',
        },
    };
});

const CustomText = styled(Text)(({ theme }) => {
    return {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    };
});

const CustomBox = styled(Box)(({ disabled, theme }) => {
    return {
        display: 'grid',
        gridTemplateColumns: '6rem 1fr',
        gridGap: theme.spacing(1, 0),
        width: '100%',
        ...(disabled && {
            opacity: 0.5,
        }),
    };
});

const CustomFormLabel = styled(FormLabel)(() => {
    return {
        alignSelf: 'center',
        width: 'fit-content',
    };
});

const CustomSelect = styled(Select)(() => {
    return {
        minWidth: 0,
        maxWidth: 'fit-content',
        '& fieldset': {
            border: 'none',
        },
    };
});

const CustomDatePicker = styled(DatePicker)(() => {
    return {
        '& fieldset': {
            border: 'none',
        },
    };
});

export default TaskSection;
